import React, { useEffect, useMemo, useRef, useState } from 'react'
import { agentColors } from './agentChatLayout'

/**
 * Searchable single-select client filter (permission-scoped list only).
 */
export default function AgentClientPicker({ clients, clientId, setClientId, disabled = false }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef(null)

  const selected = clients.find((c) => c.id === clientId)
  const displayLabel = selected ? selected.client_name || selected.id : 'All clients'

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return clients
    return clients.filter((c) => (c.client_name || c.id || '').toLowerCase().includes(q))
  }, [clients, query])

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = (id) => {
    setClientId(id)
    setQuery('')
    setOpen(false)
  }

  if (!clients.length) {
    return (
      <span style={{ fontSize: 12, color: agentColors.textMuted }} title="Org-wide docs only">
        Org-wide docs only
      </span>
    )
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', minWidth: 160, maxWidth: 220 }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          padding: '6px 10px',
          borderRadius: 6,
          border: `1px solid ${agentColors.borderSoft}`,
          background: agentColors.bgCard,
          fontSize: 12,
          color: agentColors.textPrimary,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel}
        </span>
        <span style={{ color: agentColors.textMuted, flexShrink: 0 }}>▾</span>
      </button>

      {open ? (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            marginBottom: 4,
            background: agentColors.bgCard,
            border: `1px solid ${agentColors.borderSoft}`,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(28, 25, 23, 0.12)',
            zIndex: 40,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 8, borderBottom: `1px solid ${agentColors.borderSoft}` }}>
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients…"
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: `1px solid ${agentColors.borderSoft}`,
                fontSize: 12,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto', padding: 4 }}>
            <button
              type="button"
              onClick={() => pick('')}
              style={rowStyle(clientId === '')}
            >
              All clients
            </button>
            {filtered.length === 0 ? (
              <div style={{ padding: '8px 10px', fontSize: 12, color: agentColors.textMuted }}>
                No matches
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pick(c.id)}
                  style={rowStyle(clientId === c.id)}
                >
                  {c.client_name || c.id}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function rowStyle(active) {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    border: 'none',
    borderRadius: 6,
    background: active ? '#f5f0eb' : 'transparent',
    fontSize: 12,
    color: agentColors.textPrimary,
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
}
